const STRING = { type: 'string', minLength: 1 };
const NUMBER = { type: 'number' };
const INTEGER = { type: 'integer' };
const BOOLEAN = { type: 'boolean' };
const UUID = {
  ...STRING,
  ...(process.env.NODE_ENV === 'production' ? { format: 'uuid' } : {}),
};
const DATE = { type: 'string', format: 'date' };
const ORDER = { type: 'integer', default: 0 };

const AGE = { ...INTEGER, exclusiveMinimum: 0 };
// TODO: Support non-integer weights for eg. powerlifting?
const WEIGHT = { ...INTEGER, exclusiveMinimum: 0 };
const BODYWEIGHT = { ...NUMBER, exclusiveMinimum: 0 };

const ARRAY = items => ({ type: 'array', items });
const OBJECT = $id => ({ $id, type: 'object' });
const REF = $ref => ({ anyOf: [{ $ref }, UUID, { type: 'array' }] });

// TODO: Rename relations to $relations and primaryKey to $primaryKey?

const Gender = {
  ...OBJECT('Gender'),
  required: ['uuid', 'title', 'letter'],
  properties: {
    uuid: UUID,
    order: ORDER,
    title: STRING,
    letter: STRING,
  },
};

const Lift = {
  ...OBJECT('Lift'),
  required: ['uuid', 'title', 'abbreviation', 'attemptCount'],
  properties: {
    uuid: UUID,
    order: ORDER,
    title: STRING,
    abbreviation: STRING,
    attemptCount: { ...INTEGER, minimum: 1 },
  },
};

const BodyweightCategory = {
  ...OBJECT('BodyweightCategory'),
  required: ['uuid', 'bundle', 'gender', 'title', 'abbreviation'],
  properties: {
    uuid: UUID,
    order: ORDER,
    bundle: STRING,
    gender: REF('Gender'),
    title: STRING,
    abbreviation: STRING,
    bodyweightMinimum: BODYWEIGHT,
    bodyweightMaximum: BODYWEIGHT,
  },
};

const AgeGroup = {
  ...OBJECT('AgeGroup'),
  required: [
    'uuid',
    'bundle',
    'gender',
    'title',
    'abbreviation',
    'bodyweightCategories',
  ],
  properties: {
    uuid: UUID,
    order: ORDER,
    bundle: STRING,
    gender: REF('Gender'),
    title: STRING,
    abbreviation: STRING,
    ageMinimum: AGE,
    ageMaximum: AGE,
    bodyweightCategories: ARRAY(REF('BodyweightCategory')),
  },
};

const Event = {
  ...OBJECT('Event'),
  primaryKey: ['singleton'],
  required: ['singleton', 'title', 'location', 'duration', 'mode'],
  properties: {
    singleton: STRING,
    title: STRING,
    location: STRING,
    duration: STRING,
    mode: STRING,
  },
};

const Contest = {
  ...OBJECT('Contest'),
  primaryKey: ['ageGroup', 'bodyweightCategory'],
  required: ['ageGroup', 'bodyweightCategory', 'title'],
  properties: {
    ageGroup: REF('AgeGroup'),
    bodyweightCategory: REF('BodyweightCategory'),
    order: ORDER,
    title: STRING,
    entryTotalMinimum: WEIGHT,
  },
  relations: {
    entries: { type: 'many-to-many', to: 'Entry' },
    groups: { type: 'one-to-many', to: 'Group' },
  },
};

// TODO: Add hometown/location.
const Team = {
  ...OBJECT('Team'),
  required: ['uuid', 'name'],
  properties: {
    uuid: UUID,
    name: STRING,
    abbreviation: STRING,
  },
  relations: {
    entries: { type: 'many-to-many', to: 'Entry' },
  },
};

// TODO: Log order of creation?
const Entry = {
  ...OBJECT('Entry'),
  required: ['uuid', 'teams', 'gender', 'givenName', 'contests'],
  properties: {
    uuid: UUID,
    teams: ARRAY(REF('Team')),
    gender: REF('Gender'),
    familyName: STRING,
    givenName: STRING,
    dateOfBirth: DATE,
    total: WEIGHT,
    contests: ARRAY(REF('Contest')),
  },
  relations: {
    lot: { type: 'one-to-one', to: 'Lot' },
    weighInDatum: { type: 'one-to-one', to: 'WeighInDatum' },
    weighInDeclarations: { type: 'one-to-many', to: 'WeighInDeclaration' },
    group: { type: 'many-to-one', to: 'Group' },
    athlete: { type: 'one-to-one', to: 'Athlete' },
  },
};

const Lot = {
  ...OBJECT('Lot'),
  primaryKey: ['number'],
  required: ['number', 'entry'],
  properties: {
    number: { ...INTEGER, minimum: 1 },
    entry: REF('Entry'),
  },
};

const Group = {
  ...OBJECT('Group'),
  primaryKey: ['contest', 'letter'],
  required: ['contest', 'letter'],
  properties: {
    contest: REF('Contest'),
    letter: { ...STRING, maxLength: 1 },
    order: ORDER,
    entries: ARRAY(REF('Entry')),
  },
  relations: {
    session: { type: 'many-to-one', to: 'Session' },
  },
};

const Session = {
  ...OBJECT('Session'),
  primaryKey: ['number'],
  required: ['number', 'title'],
  properties: {
    number: { ...INTEGER, minimum: 1 },
    title: STRING,
    groups: ARRAY(REF('Group')),
  },
  relations: {
    protocol: { type: 'one-to-one', to: 'Protocol' },
  },
};

const WeighInDatum = {
  ...OBJECT('WeighInDatum'),
  primaryKey: ['entry'],
  required: ['entry'],
  properties: {
    entry: REF('Entry'),
    isExcluded: BOOLEAN,
    bodyweight: BODYWEIGHT,
  },
};

const WeighInDeclaration = {
  ...OBJECT('WeighInDeclaration'),
  primaryKey: ['entry', 'lift'],
  required: ['entry', 'lift'],
  properties: {
    entry: REF('Entry'),
    lift: REF('Lift'),
    weight: WEIGHT,
  },
};

const Protocol = {
  ...OBJECT('Protocol'),
  required: ['uuid', 'title'],
  properties: {
    uuid: UUID,
    order: ORDER,
    session: REF('Session'),
    title: STRING,
  },
  relations: {
    athletes: { type: 'one-to-many', to: 'Athlete' },
  },
};

const Athlete = {
  ...OBJECT('Athlete'),
  primaryKey: ['protocol', 'startNumber'],
  required: ['protocol', 'startNumber', 'entry'],
  properties: {
    protocol: REF('Protocol'),
    startNumber: { ...INTEGER, minimum: 1 },
    entry: REF('Entry'),
  },
  relations: {
    declarations: { type: 'one-to-many', to: 'Declaration' },
    attempts: { type: 'one-to-many', to: 'Attempt' },
    results: { type: 'one-to-many', to: 'Result' },
  },
};

const Declaration = {
  ...OBJECT('Declaration'),
  primaryKey: ['athlete', 'lift', 'attemptNumber', 'changeNumber'],
  required: ['athlete', 'lift', 'attemptNumber', 'changeNumber', 'weight'],
  properties: {
    athlete: REF('Athlete'),
    lift: REF('Lift'),
    attemptNumber: { ...INTEGER, minimum: 1 },
    changeNumber: { ...INTEGER, minimum: 0 },
    weight: { ...INTEGER, minimum: 0 },
    sequenceNumber: ORDER,
  },
};

// TODO: Rename Attempt.number => attemptNumber for clarity?
const Attempt = {
  ...OBJECT('Attempt'),
  primaryKey: ['athlete', 'lift', 'number'],
  required: ['athlete', 'lift', 'number', 'weight'],
  properties: {
    athlete: REF('Athlete'),
    lift: REF('Lift'),
    number: { ...INTEGER, minimum: 1 },
    weight: WEIGHT,
    sequenceNumber: ORDER,
  },
  relations: {
    decisions: { type: 'one-to-many', to: 'Decision' },
  },
};

const Decision = {
  ...OBJECT('Decision'),
  primaryKey: ['attempt', 'origin'],
  required: ['attempt', 'origin', 'isGoodLift'],
  properties: {
    attempt: REF('Attempt'),
    order: ORDER,
    origin: { ...STRING, enum: ['manual', 'referees', 'jury'] },
    isGoodLift: { type: 'boolean' },
    sequenceNumber: ORDER,
  },
};

const Result = {
  ...OBJECT('Result'),
  primaryKey: ['athlete', 'lift'],
  required: ['athlete', 'lift', 'weight'],
  properties: {
    athlete: REF('Athlete'),
    lift: REF('Lift'),
    weight: WEIGHT,
    sequenceNumber: ORDER,
  },
};

const schemas = {
  Gender,
  Lift,
  BodyweightCategory,
  AgeGroup,
  Event,
  Contest,
  Team,
  Entry,
  Lot,
  Group,
  Session,
  WeighInDatum,
  WeighInDeclaration,
  Protocol,
  Athlete,
  Declaration,
  Attempt,
  Decision,
  Result,
};

export default schemas;



// WEBPACK FOOTER //
// ./src/schemas/schemas.js